import moment from 'moment';

export const COPILOT_FILTER_QUERY_KEY = 'copilotFilters';

/**
 * Resolves an absolute or relative backend URL into an application-relative route.
 * Strips origin (host, protocol, port) dynamically without hardcoding domains.
 */
export function resolveRedirectPath(rawUrl: string): string {
  if (!rawUrl || typeof rawUrl !== 'string') return '/';
  const trimmed = rawUrl.trim();

  try {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const parsed = new URL(trimmed);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  } catch {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }
}

/**
 * Encodes a filters object, optional apiUrl, and optional HTTP method into a safe base64 URL query component.
 */
export function encodeCopilotFilters(
  filters?: Record<string, unknown>,
  apiUrl?: string | null,
  method?: string | null,
): string {
  const hasFilters = filters && typeof filters === 'object' && Object.keys(filters).length > 0;
  const hasApiUrl = Boolean(apiUrl && typeof apiUrl === 'string' && apiUrl.trim());
  const hasMethod = Boolean(method && typeof method === 'string' && method.trim());

  if (!hasFilters && !hasApiUrl && !hasMethod) {
    return '';
  }

  const payload: Record<string, unknown> = hasFilters ? { ...filters } : {};
  if (hasApiUrl) {
    payload._copilotApiUrl = apiUrl!.trim();
  }
  if (hasMethod) {
    payload._copilotMethod = method!.trim().toUpperCase();
  }

  try {
    const jsonStr = JSON.stringify(payload);
    return encodeURIComponent(btoa(unescape(encodeURIComponent(jsonStr))));
  } catch {
    return encodeURIComponent(JSON.stringify(payload));
  }
}

/**
 * Extracts and decodes filters, dynamic apiUrl, and HTTP method from a URL search string.
 */
export function getCopilotRedirectData(searchString: string): {
  filters: Record<string, unknown> | null;
  apiUrl: string | null;
  method: string | null;
} {
  if (!searchString) return { filters: null, apiUrl: null, method: null };
  try {
    const searchParams = new URLSearchParams(searchString);
    const rawVal = searchParams.get(COPILOT_FILTER_QUERY_KEY);
    if (!rawVal) return { filters: null, apiUrl: null, method: null };

    let parsed: Record<string, unknown>;
    try {
      const decoded = decodeURIComponent(escape(atob(decodeURIComponent(rawVal))));
      parsed = JSON.parse(decoded);
    } catch {
      parsed = JSON.parse(decodeURIComponent(rawVal));
    }

    if (!parsed || typeof parsed !== 'object') {
      return { filters: null, apiUrl: null, method: null };
    }

    const apiUrl = (parsed._copilotApiUrl as string) || (parsed.api_url as string) || null;
    const method = (parsed._copilotMethod as string) || (parsed.method as string) || null;
    const { _copilotApiUrl, api_url, _copilotMethod, method: rawMethod, ...filters } = parsed;

    return {
      filters: Object.keys(filters).length > 0 ? filters : null,
      apiUrl: apiUrl || null,
      method: method ? String(method).toUpperCase() : null,
    };
  } catch {
    return { filters: null, apiUrl: null, method: null };
  }
}

/**
 * Extracts and decodes Copilot redirect filters from a URL search string or location.search.
 */
export function getCopilotRedirectFilters(searchString: string): Record<string, unknown> | null {
  return getCopilotRedirectData(searchString).filters;
}

/**
 * Extracts and decodes Copilot dynamic API URL from a URL search string or location.search.
 */
export function getCopilotRedirectApiUrl(searchString: string): string | null {
  return getCopilotRedirectData(searchString).apiUrl;
}

/**
 * Extracts and decodes Copilot HTTP method from a URL search string or location.search.
 */
export function getCopilotRedirectMethod(searchString: string): string | null {
  return getCopilotRedirectData(searchString).method;
}

/**
 * Parses an apiUrl string into its base endpoint path (without query params) and an object of query parameters.
 */
export function parseApiUrl(apiUrl?: string | null): {
  basePath: string | null;
  queryParams: Record<string, unknown>;
} {
  if (!apiUrl || typeof apiUrl !== 'string') {
    return { basePath: null, queryParams: {} };
  }

  const trimmed = apiUrl.trim();
  if (!trimmed) {
    return { basePath: null, queryParams: {} };
  }

  const queryParams: Record<string, unknown> = {};
  const [basePath, search] = trimmed.split('?');

  if (search) {
    const searchParams = new URLSearchParams(search);
    searchParams.forEach((value, key) => {
      const isArrayKey = key.endsWith('[]');
      const cleanKey = isArrayKey ? key.slice(0, -2) : key;

      let parsedValue: unknown = value;
      if (value === 'true') parsedValue = true;
      else if (value === 'false') parsedValue = false;
      else if (/^\d+$/.test(value) && !value.startsWith('0')) {
        parsedValue = Number(value);
      }

      if (isArrayKey) {
        if (!queryParams[cleanKey]) {
          queryParams[cleanKey] = [];
        }
        (queryParams[cleanKey] as unknown[]).push(parsedValue);
      } else if (queryParams[cleanKey] !== undefined) {
        if (Array.isArray(queryParams[cleanKey])) {
          (queryParams[cleanKey] as unknown[]).push(parsedValue);
        } else {
          queryParams[cleanKey] = [queryParams[cleanKey], parsedValue];
        }
      } else {
        queryParams[cleanKey] = parsedValue;
      }
    });
  }

  return {
    basePath: basePath || null,
    queryParams,
  };
}

/**
 * Builds the complete relative URL with encoded filters, dynamic apiUrl, and optional method attached in query params.
 */
export function buildRedirectUrl(
  rawUrl: string,
  filters?: Record<string, unknown>,
  apiUrl?: string | null,
  method?: string | null,
): string {
  const relativePath = resolveRedirectPath(rawUrl);
  const hasFilters = filters && typeof filters === 'object' && Object.keys(filters).length > 0;
  const hasApiUrl = Boolean(apiUrl && typeof apiUrl === 'string' && apiUrl.trim());
  const hasMethod = Boolean(method && typeof method === 'string' && method.trim());

  if (!hasFilters && !hasApiUrl && !hasMethod) {
    return relativePath;
  }

  const encoded = encodeCopilotFilters(filters, apiUrl, method);
  if (!encoded) return relativePath;

  const [pathAndQuery, hash] = relativePath.split('#');
  const separator = pathAndQuery.includes('?') ? '&' : '?';
  const finalPath = `${pathAndQuery}${separator}${COPILOT_FILTER_QUERY_KEY}=${encoded}`;

  return hash ? `${finalPath}#${hash}` : finalPath;
}

/**
 * Derives user-facing title, CTA text, subtitle, and icon type from the redirect URL.
 */
export function getRedirectTitleAndLabel(rawUrl: string): {
  title: string;
  ctaText: string;
  subtitle: string;
  iconType: 'invoice' | 'transaction' | 'shipment' | 'default';
} {
  const path = resolveRedirectPath(rawUrl).toLowerCase();

  if (path.includes('invoice')) {
    return {
      title: 'View Invoices',
      ctaText: 'Open Invoices',
      subtitle: 'View filtered invoice list',
      iconType: 'invoice',
    };
  }

  if (path.includes('transaction')) {
    return {
      title: 'View Transactions',
      ctaText: 'Open Transactions',
      subtitle: 'View filtered transaction list',
      iconType: 'transaction',
    };
  }

  if (path.includes('shipment')) {
    return {
      title: 'View Shipments',
      ctaText: 'Open Shipments',
      subtitle: 'View filtered shipment list',
      iconType: 'shipment',
    };
  }

  return {
    title: 'Open Results',
    ctaText: 'Open Results',
    subtitle: 'View filtered results',
    iconType: 'default',
  };
}

/**
 * Formats arbitrary redirect filters into clean display chips/summaries for the Copilot UI.
 */
export function formatFilterSummary(filters?: Record<string, unknown>): Array<{ label: string; value: string }> {
  if (!filters || typeof filters !== 'object') return [];

  const summaries: Array<{ label: string; value: string }> = [];

  // Date Range formatting
  const fromDate = filters.fromDate ? String(filters.fromDate) : null;
  const toDate = filters.toDate ? String(filters.toDate) : null;

  if (fromDate && toDate) {
    const formattedFrom = moment(fromDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).isValid()
      ? moment(fromDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('DD MMM YYYY')
      : fromDate;
    const formattedTo = moment(toDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).isValid()
      ? moment(toDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('DD MMM YYYY')
      : toDate;

    summaries.push({
      label: 'Date',
      value: `${formattedFrom} → ${formattedTo}`,
    });
  } else if (fromDate) {
    const formattedFrom = moment(fromDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).isValid()
      ? moment(fromDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('DD MMM YYYY')
      : fromDate;
    summaries.push({ label: 'From Date', value: formattedFrom });
  } else if (toDate) {
    const formattedTo = moment(toDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).isValid()
      ? moment(toDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('DD MMM YYYY')
      : toDate;
    summaries.push({ label: 'To Date', value: formattedTo });
  }

  // Invoice type
  const invoiceType = filters.invoice_type || filters.invoiceType;
  if (invoiceType) {
    const types = Array.isArray(invoiceType) ? invoiceType.join(', ') : String(invoiceType);
    summaries.push({ label: 'Type', value: types });
  }

  // Status
  if (filters.status) {
    const statusVal = Array.isArray(filters.status) ? filters.status.join(', ') : String(filters.status);
    summaries.push({ label: 'Status', value: statusVal });
  }

  // Mode (for Transactions)
  if (filters.mode) {
    const modeVal = Array.isArray(filters.mode) ? filters.mode.join(', ') : String(filters.mode);
    summaries.push({ label: 'Mode', value: modeVal });
  }

  // Transaction Type (credit / debit)
  if (filters.type && !filters.invoice_type) {
    const typeVal = Array.isArray(filters.type) ? filters.type.join(', ') : String(filters.type);
    summaries.push({ label: 'Transaction Type', value: typeVal });
  }

  // Account Type
  if (filters.accountType) {
    summaries.push({ label: 'Account Type', value: String(filters.accountType) });
  }

  // Search Key / Value
  if (filters.searchKey && filters.searchValue) {
    const readableKey = String(filters.searchKey)
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .trim();
    summaries.push({
      label: readableKey.charAt(0).toUpperCase() + readableKey.slice(1),
      value: String(filters.searchValue),
    });
  }

  // Systems
  if (filters.systems && Array.isArray(filters.systems) && filters.systems.length > 0) {
    summaries.push({ label: 'Systems', value: `${filters.systems.length} selected` });
  }

  // Nested filters array (e.g. for POST queries with { entity: 'Shipment', filters: [...] })
  if (Array.isArray(filters.filters)) {
    filters.filters.forEach((item: any) => {
      if (item && item.field && item.value !== undefined) {
        const readableField = String(item.field)
          .replace(/\./g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .trim();
        const formattedField = readableField
          .split(' ')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        summaries.push({
          label: formattedField,
          value: String(item.value),
        });
      }
    });
  }

  return summaries;
}
